import React, { useState, useEffect } from 'react'
import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Heading, 
  SimpleGrid, 
  Select,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Alert,
  AlertIcon,
  useColorModeValue,
  IconButton
} from '@chakra-ui/react'
import { FaSync } from 'react-icons/fa'
import { adminService, subscriptionService, promoCodeService } from '@/services/firestore'

interface PromoCodeUsage {
  promoCode: string
  promoCodeId: string
  discountPercentage: number
  originalAmount: number
  discountAmount: number
  appliedAt: Date
  userId: string
  userEmail: string
  subscriptionCode: string
  planName: string
}

interface MonthlyBreakdown {
  month: string
  totalDiscount: number
  usageCount: number
  uniqueUsers: number
  promoCodeDetails: Record<string, {
    code: string
    discountPercentage: number
    usageCount: number
    totalDiscount: number
    users: string[]
  }>
}

export const PromoCodeAuditTrail: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [promoCodeUsage, setPromoCodeUsage] = useState<PromoCodeUsage[]>([])
  const [availableMonths, setAvailableMonths] = useState<string[]>([])
  const [selectedMonth, setSelectedMonth] = useState('')
  const [monthlyBreakdown, setMonthlyBreakdown] = useState<MonthlyBreakdown[]>([])
  const [availablePromoCodes, setAvailablePromoCodes] = useState<string[]>([])
  const [selectedPromoCode, setSelectedPromoCode] = useState('')

  const bgColor = useColorModeValue('#191919', '#191919')
  const textColor = useColorModeValue('white', 'white')
  const borderColor = useColorModeValue('gray.700', 'gray.700')
  const accentColor = '#3182ce'

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const loadPromoCodeUsage = async () => {
    try {
      setIsLoading(true)
      
      // Fetch all data
      const subscriptions = await subscriptionService.getAllSubscriptions()
      const promoCodes = await promoCodeService.getPromoCodes()
      const users = await adminService.getAllUsers()

      // Create user email map
      const userEmailMap = new Map()
      users.forEach(user => {
        userEmailMap.set(user.uid, user.email || 'unknown')
      })

      // Process promo code usage
      const usage: PromoCodeUsage[] = []
      const monthSet = new Set<string>()

      subscriptions.forEach(subscription => {
        if (subscription.appliedPromoCode && subscription.appliedPromoCode.appliedAt) {
          try {
            const appliedAt = subscription.appliedPromoCode.appliedAt instanceof Date 
              ? subscription.appliedPromoCode.appliedAt 
              : new Date(subscription.appliedPromoCode.appliedAt.seconds * 1000)
            
            const monthKey = `${appliedAt.getFullYear()}-${String(appliedAt.getMonth() + 1).padStart(2, '0')}`
            monthSet.add(monthKey)

            usage.push({
              promoCode: subscription.appliedPromoCode.code,
              promoCodeId: subscription.appliedPromoCode.id,
              discountPercentage: subscription.appliedPromoCode.discountPercentage,
              originalAmount: subscription.originalAmount || 0,
              discountAmount: subscription.discountAmount || 0,
              appliedAt,
              userId: subscription.userId || 'unknown',
              userEmail: userEmailMap.get(subscription.userId || '') || 'unknown',
              subscriptionCode: subscription.paystackSubscriptionId || 'unknown',
              planName: subscription.planType || 'unknown'
            })
          } catch (error) {
            console.error('Error processing subscription promo code data:', error, subscription)
          }
        }
      })

      setPromoCodeUsage(usage)
      setAvailableMonths(Array.from(monthSet).sort().reverse())

      // Extract unique promo codes
      const promoCodeSet = new Set(usage.map(u => u.promoCode))
      setAvailablePromoCodes(Array.from(promoCodeSet).sort())

      // Create monthly breakdown
      const breakdown: MonthlyBreakdown[] = []
      monthSet.forEach(month => {
        const monthUsage = usage.filter(u => {
          const usageMonth = `${u.appliedAt.getFullYear()}-${String(u.appliedAt.getMonth() + 1).padStart(2, '0')}`
          return usageMonth === month
        })

        const uniqueUsers = new Set(monthUsage.map(u => u.userId))
        const promoCodeDetails: Record<string, any> = {}
        let totalDiscount = 0

        monthUsage.forEach(u => {
          totalDiscount += u.discountAmount
          
          if (!promoCodeDetails[u.promoCodeId]) {
            promoCodeDetails[u.promoCodeId] = {
              code: u.promoCode,
              discountPercentage: u.discountPercentage,
              usageCount: 0,
              totalDiscount: 0,
              users: []
            }
          }
          
          promoCodeDetails[u.promoCodeId].usageCount++
          promoCodeDetails[u.promoCodeId].totalDiscount += u.discountAmount
          if (!promoCodeDetails[u.promoCodeId].users.includes(u.userId)) {
            promoCodeDetails[u.promoCodeId].users.push(u.userId)
          }
        })

        breakdown.push({
          month,
          totalDiscount,
          usageCount: monthUsage.length,
          uniqueUsers: uniqueUsers.size,
          promoCodeDetails
        })
      })

      setMonthlyBreakdown(breakdown.sort((a, b) => b.month.localeCompare(a.month)))
    } catch (error) {
      console.error('Error loading promo code usage:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPromoCodeUsage()
  }, [])

  if (isLoading) {
    return (
      <Box py={10}>
        <Text size="xl" color={accentColor}>Loading...</Text>
      </Box>
    )
  }

  const filteredUsage = promoCodeUsage.filter(u => {
    // Filter by month if selected
    if (selectedMonth) {
      const usageMonth = `${u.appliedAt.getFullYear()}-${String(u.appliedAt.getMonth() + 1).padStart(2, '0')}`
      if (usageMonth !== selectedMonth) return false
    }
    
    // Filter by promo code if selected
    if (selectedPromoCode && u.promoCode !== selectedPromoCode) {
      return false
    }
    
    return true
  })

  const currentMonth = new Date().toISOString().slice(0, 7)
  const currentMonthData = monthlyBreakdown.find(b => b.month === currentMonth) || {
    month: currentMonth,
    totalDiscount: 0,
    usageCount: 0,
    uniqueUsers: 0,
    promoCodeDetails: {}
  }

  const totals = {
    totalDiscount: monthlyBreakdown.reduce((sum, b) => sum + b.totalDiscount, 0),
    totalUsage: monthlyBreakdown.reduce((sum, b) => sum + b.usageCount, 0),
    totalUniqueUsers: new Set(promoCodeUsage.map(u => u.userId)).size
  }

  // Calculate filtered totals
  const filteredTotals = {
    totalDiscount: filteredUsage.reduce((sum, u) => sum + u.discountAmount, 0),
    totalUsage: filteredUsage.length,
    totalUniqueUsers: new Set(filteredUsage.map(u => u.userId)).size
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" w="full">
        <Heading size="md" color={accentColor}>
          Promo Code Audit Trail
        </Heading>
        <IconButton
          aria-label="Refresh data"
          icon={<FaSync />}
          onClick={loadPromoCodeUsage}
          isLoading={isLoading}
          colorScheme="blue"
          size="sm"
        />
      </HStack>

      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        <Box bg={bgColor} p={6} borderRadius="xl" border="1px solid" borderColor={borderColor}>
          <VStack>
            <Text color="gray.400">
              {selectedPromoCode || selectedMonth ? 'Filtered Discount' : 'Total Discount Given'}
            </Text>
            <Text color="green.400" fontSize="xl" fontWeight="bold">
              {formatCurrency(selectedPromoCode || selectedMonth ? filteredTotals.totalDiscount : totals.totalDiscount)}
            </Text>
            <Text color="gray.500" fontSize="sm">
              {selectedPromoCode || selectedMonth ? 'Filtered results' : 'All time'}
            </Text>
          </VStack>
        </Box>

        <Box bg={bgColor} p={6} borderRadius="xl" border="1px solid" borderColor={borderColor}>
          <VStack>
            <Text color="gray.400">
              {selectedPromoCode || selectedMonth ? 'Filtered Usage' : 'Total Usage'}
            </Text>
            <Text color={accentColor} fontSize="xl" fontWeight="bold">
              {selectedPromoCode || selectedMonth ? filteredTotals.totalUsage : totals.totalUsage}
            </Text>
            <Text color="gray.500" fontSize="sm">
              {selectedPromoCode || selectedMonth ? 'Filtered results' : 'All time'}
            </Text>
          </VStack>
        </Box>

        <Box bg={bgColor} p={6} borderRadius="xl" border="1px solid" borderColor={borderColor}>
          <VStack>
            <Text color="gray.400">
              {selectedPromoCode || selectedMonth ? 'Filtered Users' : 'Unique Users'}
            </Text>
            <Text color="purple.400" fontSize="xl" fontWeight="bold">
              {selectedPromoCode || selectedMonth ? filteredTotals.totalUniqueUsers : totals.totalUniqueUsers}
            </Text>
            <Text color="gray.500" fontSize="sm">
              {selectedPromoCode || selectedMonth ? 'Filtered results' : 'All time'}
            </Text>
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Current Month Summary */}
      <Box bg={bgColor} p={6} borderRadius="xl" border="1px solid" borderColor={borderColor}>
        <VStack spacing={4} align="stretch">
          <Heading size="sm" color={textColor}>
            Current Month ({new Date().toLocaleDateString('en-ZA', {
              year: 'numeric',
              month: 'long'
            })})
          </Heading>
          
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <VStack>
              <Text color="gray.400">Monthly Discount</Text>
              <Text color="green.400" fontSize="lg" fontWeight="bold">
                {formatCurrency(currentMonthData.totalDiscount)}
              </Text>
            </VStack>
            
            <VStack>
              <Text color="gray.400">Monthly Usage</Text>
              <Text color={accentColor} fontSize="lg" fontWeight="bold">
                {currentMonthData.usageCount}
              </Text>
            </VStack>
            
            <VStack>
              <Text color="gray.400">Monthly Users</Text>
              <Text color="purple.400" fontSize="lg" fontWeight="bold">
                {currentMonthData.uniqueUsers}
              </Text>
            </VStack>
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Filters */}
      <HStack spacing={4} flexWrap="wrap">
        <HStack maxW="300px">
          <Text color="gray.300">Filter by Month</Text>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            bg={bgColor}
            color={textColor}
            borderColor={borderColor}
          >
            <option value="">All Months</option>
            {availableMonths.map(month => (
              <option key={month} value={month}>
                {new Date(month + '-01').toLocaleDateString('en-ZA', {
                  year: 'numeric',
                  month: 'long'
                })}
              </option>
            ))}
          </Select>
        </HStack>

        <HStack maxW="300px">
          <Text color="gray.300">Filter by Promo Code</Text>
          <Select
            value={selectedPromoCode}
            onChange={(e) => setSelectedPromoCode(e.target.value)}
            bg={bgColor}
            color={textColor}
            borderColor={borderColor}
          >
            <option value="">All Promo Codes</option>
            {availablePromoCodes.map(code => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </HStack>
      </HStack>

      {/* Usage Details Table */}
      <Box bg={bgColor} p={6} borderRadius="xl" border="1px solid" borderColor={borderColor}>
        <VStack spacing={4} align="stretch">
          <HStack justify="space-between">
            <Heading size="sm" color={textColor}>
              Promo Code Usage Details
            </Heading>
            <Badge colorScheme="blue" fontSize="sm">
              {filteredUsage.length} records
            </Badge>
          </HStack>

          {filteredUsage.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              No promo code usage found for the selected period.
            </Alert>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th color="gray.300">Date Applied</Th>
                    <Th color="gray.300">Promo Code</Th>
                    <Th color="gray.300">User Email</Th>
                    <Th color="gray.300">Plan</Th>
                    <Th color="gray.300">Original Amount</Th>
                    <Th color="gray.300">Discount</Th>
                    <Th color="gray.300">Discount %</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredUsage.map((usage, index) => (
                    <Tr key={index}>
                      <Td color="gray.300">
                        {new Intl.DateTimeFormat('en-ZA', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }).format(usage.appliedAt)}
                      </Td>
                      <Td>
                        <Badge colorScheme="green" fontSize="xs">
                          {usage.promoCode}
                        </Badge>
                      </Td>
                      <Td color="gray.300" fontSize="sm">
                        {usage.userEmail}
                      </Td>
                      <Td color="gray.300" fontSize="sm">
                        {usage.planName}
                      </Td>
                      <Td color="gray.300">
                        {formatCurrency(usage.originalAmount)}
                      </Td>
                      <Td color="green.400" fontWeight="medium">
                        {formatCurrency(usage.discountAmount)}
                      </Td>
                      <Td color="gray.300">
                        {usage.discountPercentage}%
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </VStack>
      </Box>

      {/* Monthly Breakdown Table */}
      <Box bg={bgColor} p={6} borderRadius="xl" border="1px solid" borderColor={borderColor}>
        <VStack spacing={4} align="stretch">
          <Heading size="sm" color={textColor}>
            Monthly Breakdown
          </Heading>
          
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th color="gray.300">Month</Th>
                  <Th color="gray.300">Total Discount</Th>
                  <Th color="gray.300">Usage Count</Th>
                  <Th color="gray.300">Unique Users</Th>
                  <Th color="gray.300">Promo Codes Used</Th>
                </Tr>
              </Thead>
              <Tbody>
                {monthlyBreakdown.map((breakdown) => (
                  <Tr key={breakdown.month}>
                    <Td color="gray.300">
                      {new Date(breakdown.month + '-01').toLocaleDateString('en-ZA', {
                        year: 'numeric',
                        month: 'long'
                      })}
                    </Td>
                    <Td color="green.400" fontWeight="medium">
                      {formatCurrency(breakdown.totalDiscount)}
                    </Td>
                    <Td color={accentColor}>
                      {breakdown.usageCount}
                    </Td>
                    <Td color="purple.400">
                      {breakdown.uniqueUsers}
                    </Td>
                    <Td color="gray.300">
                      {Object.keys(breakdown.promoCodeDetails).map(codeId => (
                        <Badge
                          key={codeId}
                          colorScheme="green"
                          fontSize="xs"
                          mr={1}
                        >
                          {breakdown.promoCodeDetails[codeId].code}
                        </Badge>
                      ))}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      </Box>
    </VStack>
  )
} 